import React, { Component, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { captureError } from '@/lib/monitoring/errorMonitor';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const RELOAD_GUARD_KEY = 'chunk-reload-attempts';
const RELOAD_GUARD_TTL_MS = 15_000;

interface ReloadAttempt {
  signature: string;
  timestamp: number;
}

/**
 * Detect stale lazy-loaded chunk / dynamic import failures. These typically
 * happen after a deploy or Vite dependency re-optimisation invalidates the
 * hashed chunk URLs the open tab is still holding references to.
 */
export function isChunkLoadError(message: string | undefined | null): boolean {
  if (!message) return false;
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /Loading CSS chunk \d+ failed/i.test(message)
  );
}

/**
 * Build a stable signature for an error so we can detect "same failure retried
 * very recently" without blocking recovery from genuinely new stale-chunk
 * events later in the tab's lifetime.
 */
function errorSignature(error: Error): string {
  const message = error?.message ?? 'unknown';
  // Try to pull out the failing module URL when present – that's the most
  // stable identifier across re-renders.
  const urlMatch = message.match(/https?:\/\/\S+/);
  return urlMatch ? urlMatch[0] : message.slice(0, 200);
}

function readReloadAttempts(): ReloadAttempt[] {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter(
      (entry): entry is ReloadAttempt =>
        entry &&
        typeof entry.signature === 'string' &&
        typeof entry.timestamp === 'number' &&
        now - entry.timestamp < RELOAD_GUARD_TTL_MS,
    );
  } catch {
    return [];
  }
}

function recordReloadAttempt(signature: string): void {
  try {
    const attempts = readReloadAttempts();
    attempts.push({ signature, timestamp: Date.now() });
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(attempts));
  } catch {
    // sessionStorage unavailable (private mode etc.) – ignore.
  }
}

function hasRecentReloadAttempt(signature: string): boolean {
  return readReloadAttempts().some((entry) => entry.signature === signature);
}

/**
 * Error boundary component that catches JavaScript errors in child components.
 * Prevents white screens by displaying a fallback UI and providing retry capability.
 *
 * Also auto-recovers from stale lazy-loaded chunk errors (common after deploys
 * or Vite dependency re-optimisation) via a one-shot reload, guarded by a
 * short-lived per-failure record in sessionStorage so we never loop.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
    void captureError({
      feature: 'app',
      error,
      context: { boundary: 'root', chunkError: isChunkLoadError(error?.message) },
    });

    if (isChunkLoadError(error?.message) && typeof window !== 'undefined') {
      const signature = errorSignature(error);
      if (!hasRecentReloadAttempt(signature)) {
        recordReloadAttempt(signature);
        console.warn('[ErrorBoundary] Stale chunk detected — reloading once.');
        window.location.reload();
      } else {
        console.warn(
          '[ErrorBoundary] Stale chunk reload already attempted recently — showing fallback.',
        );
      }
    }
  }

  handleRetry = (): void => {
    // For chunk-load errors, resetting state isn't enough: React.lazy caches
    // the rejected import, so we need a real page reload to fetch fresh
    // module URLs.
    if (this.state.error && isChunkLoadError(this.state.error.message)) {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = isChunkLoadError(this.state.error?.message);

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {isChunkError ? 'Page failed to load' : 'Something went wrong'}
            </h2>
            
            <p className="text-muted-foreground mb-6">
              {isChunkError 
                ? 'There was a problem loading this page. This can happen after an update. Please try refreshing.'
                : 'An unexpected error occurred. Please try again or refresh the page.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleRetry} variant="default">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try again
              </Button>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
              >
                Refresh page
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Error details
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto max-h-40">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
